import React, { useState } from "react";
import {
  Team,
  CourtConfiguration,
  PlayerConfiguration,
} from "../types/tournament";
import { createTeam } from "../utils/tournamentLogic";
import { Plus, X, Users, Sparkles } from "lucide-react";

interface TournamentSetupProps {
  onStart: (
    name: string,
    teams: Team[],
    courts: CourtConfiguration[],
    playerConfig: PlayerConfiguration,
    rounds: number,
    tournamentType: "speed_ladder" | "single_elimination" | "classic_ladder"
  ) => void;
}

const TournamentSetup: React.FC<TournamentSetupProps> = ({ onStart }) => {
  const [tournamentName, setTournamentName] = useState("");
  const [rounds, setRounds] = useState(6);
  const [teams, setTeams] = useState<Team[]>([]);
  const [showAddTeam, setShowAddTeam] = useState(false);
  const [newTeamName, setNewTeamName] = useState("");
  const [newPlayer1, setNewPlayer1] = useState("");
  const [newPlayer2, setNewPlayer2] = useState("");
  const [autoGenerateCount, setAutoGenerateCount] = useState(16);
  const [showAutoGenerate, setShowAutoGenerate] = useState(false);
  const [tournamentType, setTournamentType] = useState<
    "speed_ladder" | "single_elimination" | "classic_ladder"
  >("classic_ladder");

  const maxTeams =
    tournamentType === "speed_ladder"
      ? 16
      : tournamentType === "classic_ladder"
      ? 16
      : 64; // Speed ladder & Classic: 16 teams max, Bracket: up to 64 teams
  const canStart =
    tournamentName && teams.length >= 2 && teams.length <= maxTeams;

  const addTeam = () => {
    if (newTeamName && newPlayer1 && newPlayer2) {
      const team = createTeam(newTeamName, newPlayer1, newPlayer2);
      setTeams([...teams, team]);
      setNewTeamName("");
      setNewPlayer1("");
      setNewPlayer2("");
      setShowAddTeam(false);
    }
  };

  const removeTeam = (id: string) => {
    setTeams(teams.filter((team) => team.id !== id));
  };

  const generateTeamNames = (count: number): string[] => {
    const names: string[] = [];

    // Generate names based on tournament name or use generic themes
    const baseName = tournamentName.trim() || "Team";
    const themes = [
      "Thunder",
      "Lightning",
      "Storm",
      "Fire",
      "Ice",
      "Wind",
      "Earth",
      "Water",
      "Phoenix",
      "Dragon",
      "Eagle",
      "Hawk",
      "Lion",
      "Tiger",
      "Bear",
      "Wolf",
      "Alpha",
      "Beta",
      "Gamma",
      "Delta",
      "Omega",
      "Sigma",
      "Zeta",
      "Theta",
      "Red",
      "Blue",
      "Green",
      "Yellow",
      "Purple",
      "Orange",
      "Pink",
      "Black",
      "Swift",
      "Fast",
      "Quick",
      "Rapid",
      "Speed",
      "Velocity",
      "Momentum",
      "Power",
    ];

    for (let i = 0; i < count; i++) {
      if (i < themes.length) {
        names.push(`${themes[i]} ${baseName}s`);
      } else {
        names.push(`${baseName} ${i + 1}`);
      }
    }

    return names;
  };

  const generatePlayerNames = (teamName: string, playerNum: number): string => {
    const playerThemes = [
      "Ace",
      "King",
      "Queen",
      "Jack",
      "Spade",
      "Heart",
      "Diamond",
      "Club",
      "Alpha",
      "Bravo",
      "Charlie",
      "Delta",
      "Echo",
      "Foxtrot",
      "Golf",
      "Hotel",
      "Red",
      "Blue",
      "Green",
      "Yellow",
      "Purple",
      "Orange",
      "Pink",
      "Black",
      "North",
      "South",
      "East",
      "West",
      "Northeast",
      "Northwest",
      "Southeast",
      "Southwest",
    ];

    const index = teams.length + playerNum - 1;
    if (index < playerThemes.length) {
      return `${playerThemes[index]} ${teamName.split(" ")[0]}`;
    }
    return `Player ${playerNum} ${teamName.split(" ")[0]}`;
  };

  const autoGenerateTeams = () => {
    if (autoGenerateCount > 0 && autoGenerateCount <= maxTeams) {
      const teamNames = generateTeamNames(autoGenerateCount);
      const newTeams: Team[] = [];

      teamNames.forEach((teamName, index) => {
        const player1 = generatePlayerNames(teamName, 1);
        const player2 = generatePlayerNames(teamName, 2);
        const team = createTeam(teamName, player1, player2);
        newTeams.push(team);
      });

      setTeams([...teams, ...newTeams]);
      setShowAutoGenerate(false);
      setAutoGenerateCount(
        Math.min(autoGenerateCount, maxTeams - teams.length)
      );
    }
  };

  const clearAllTeams = () => {
    setTeams([]);
  };

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6">
      <div className="text-center mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
          🏓 Speed Ladder Tournament Setup
        </h1>
        <p className="text-base sm:text-lg text-gray-600">
          Configure your doubles pickleball tournament
        </p>
      </div>

      <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6 lg:p-8">
        {/* Tournament Details */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">
            Tournament Details
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tournament Name
              </label>
              <input
                type="text"
                value={tournamentName}
                onChange={(e) => setTournamentName(e.target.value)}
                placeholder="e.g., Summer Championship 2024"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Number of Rounds
              </label>
              <select
                value={rounds}
                onChange={(e) => setRounds(Number(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                {[3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                  <option key={num} value={num}>
                    {num} Rounds
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tournament Type
              </label>
              <select
                value={tournamentType}
                onChange={(e) =>
                  setTournamentType(
                    e.target.value as
                      | "speed_ladder"
                      | "single_elimination"
                      | "classic_ladder"
                  )
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="classic_ladder">
                  Classic Speed Ladder (Recommended)
                </option>
                <option value="speed_ladder">
                  Speed Ladder Doubles (Court-based)
                </option>
                <option value="single_elimination">
                  Single Elimination Bracket
                </option>
              </select>

              {/* Tournament Type Description */}
              <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-md">
                <div className="text-sm text-blue-800">
                  {tournamentType === "classic_ladder" && (
                    <>
                      <strong>Classic Speed Ladder:</strong> Perfect for 16-20
                      teams. Winners move UP one court, losers move DOWN. Battle
                      to reach and defend Court 1! Everyone plays 6-8 games
                      guaranteed.
                    </>
                  )}
                  {tournamentType === "speed_ladder" && (
                    <>
                      <strong>Speed Ladder Doubles:</strong> Teams move up/down
                      courts based on wins/losses with advanced features like
                      achievements and live dashboard. Great for smaller groups.
                    </>
                  )}
                  {tournamentType === "single_elimination" && (
                    <>
                      <strong>Single Elimination Bracket:</strong> Ideal for 24+
                      teams. Pure knockout tournament - lose once and you're
                      out! Quick completion in 2-3 hours.
                    </>
                  )}
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Maximum Teams Allowed
              </label>
              <div className="px-3 py-2 bg-gray-100 border border-gray-300 rounded-md text-gray-700">
                {maxTeams} teams{" "}
                {tournamentType === "speed_ladder" ||
                tournamentType === "classic_ladder"
                  ? "(16 teams maximum)"
                  : "(bracket format)"}
              </div>
            </div>
          </div>
        </div>

        {/* Teams Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold text-gray-900 flex items-center">
              <Users className="w-5 h-5 mr-2" />
              Teams ({teams.length}/{maxTeams})
            </h3>
            <div className="flex space-x-2">
              <button
                type="button"
                onClick={() => setShowAutoGenerate(true)}
                disabled={teams.length >= maxTeams}
                className={`px-4 py-2 rounded-md flex items-center ${
                  teams.length >= maxTeams
                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                    : "bg-green-600 text-white hover:bg-green-700"
                }`}
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Auto Generate
              </button>
              <button
                type="button"
                onClick={() => setShowAddTeam(true)}
                disabled={teams.length >= maxTeams}
                className={`px-4 py-2 rounded-md flex items-center ${
                  teams.length >= maxTeams
                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                    : "bg-primary-600 text-white hover:bg-primary-700"
                }`}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Team
              </button>
            </div>
          </div>

          {/* Auto Generate Modal */}
          {showAutoGenerate && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg p-6 w-full max-w-md">
                <h3 className="text-lg font-semibold mb-4">
                  Auto Generate Teams
                </h3>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Number of Teams to Generate
                  </label>
                  <input
                    type="number"
                    min="1"
                    max={maxTeams - teams.length}
                    value={autoGenerateCount}
                    onChange={(e) =>
                      setAutoGenerateCount(Number(e.target.value))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    Max: {maxTeams - teams.length} teams
                  </p>
                </div>
                <div className="mb-4 p-3 bg-blue-50 rounded-md">
                  <p className="text-sm text-blue-800">
                    <strong>Preview:</strong> Teams will be named using themes
                    like "Thunder", "Lightning", "Storm", etc.
                    {tournamentName && ` based on "${tournamentName}"`}
                  </p>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={autoGenerateTeams}
                    className="flex-1 bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700"
                  >
                    Generate Teams
                  </button>
                  <button
                    onClick={() => setShowAutoGenerate(false)}
                    className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Add Team Modal */}
          {showAddTeam && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg p-6 w-full max-w-md">
                <h3 className="text-lg font-semibold mb-4">Add New Team</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Team Name
                    </label>
                    <input
                      type="text"
                      value={newTeamName}
                      onChange={(e) => setNewTeamName(e.target.value)}
                      placeholder="e.g., Thunder Bolts"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Player 1
                    </label>
                    <input
                      type="text"
                      value={newPlayer1}
                      onChange={(e) => setNewPlayer1(e.target.value)}
                      placeholder="e.g., John Smith"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Player 2
                    </label>
                    <input
                      type="text"
                      value={newPlayer2}
                      onChange={(e) => setNewPlayer2(e.target.value)}
                      placeholder="e.g., Jane Doe"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                </div>
                <div className="flex space-x-2 mt-6">
                  <button
                    onClick={addTeam}
                    className="flex-1 bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700"
                  >
                    Add Team
                  </button>
                  <button
                    onClick={() => setShowAddTeam(false)}
                    className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Teams List */}
          {teams.length > 0 && (
            <div className="space-y-3">
              {teams.map((team) => (
                <div
                  key={team.id}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                >
                  <div>
                    <div className="font-semibold text-gray-900">
                      {team.name}
                    </div>
                    <div className="text-sm text-gray-600">
                      {team.player1} & {team.player2}
                    </div>
                  </div>
                  <button
                    onClick={() => removeTeam(team.id)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              ))}

              <div className="flex justify-between items-center pt-4 border-t">
                <button
                  onClick={clearAllTeams}
                  className="text-red-600 hover:text-red-800 text-sm"
                >
                  Clear All Teams
                </button>
                <div className="text-sm text-gray-500">
                  {teams.length} of {maxTeams} teams
                </div>
              </div>
            </div>
          )}

          {teams.length === 0 && (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">No teams added yet</p>
              <p className="text-gray-400 text-sm mt-2">
                Use "Auto Generate" for quick setup or "Add Team" for manual
                entry
              </p>
            </div>
          )}
        </div>

        {/* Start Tournament */}
        <div className="text-center">
          <button
            onClick={() => {
              // Generate courts based on tournament type and teams
              const calculateCourts = () => {
                if (tournamentType === "speed_ladder") {
                  // For Speed Ladder Doubles, calculate courts based on teams
                  const numCourts = Math.ceil(teams.length / 2);
                  return Array.from({ length: numCourts }, (_, i) => ({
                    id: `court-${i + 1}`,
                    name: `Court ${i + 1}`,
                    isActive: true,
                  }));
                }
                // For other tournament types, return empty array (they handle courts differently)
                return [];
              };

              onStart(
                tournamentName,
                teams,
                calculateCourts(),
                {
                  playersPerTeam: 2,
                  minTeamsRequired: 4,
                  maxTeamsAllowed: maxTeams,
                  totalPlayersExpected: teams.length * 2,
                  allowSubstitutes: false,
                  maxSubstitutesPerTeam: 0,
                },
                rounds,
                tournamentType
              );
            }}
            disabled={!canStart}
            className={`px-8 py-3 rounded-md text-lg font-semibold ${
              canStart
                ? "bg-primary-600 text-white hover:bg-primary-700"
                : "bg-gray-300 text-gray-500 cursor-not-allowed"
            }`}
          >
            Start Tournament
          </button>

          {!canStart && (
            <p className="text-sm text-gray-500 mt-2">
              {!tournamentName
                ? "Please enter a tournament name"
                : teams.length < 2
                ? "Please add at least 2 teams"
                : teams.length > maxTeams
                ? `Maximum ${maxTeams} teams allowed`
                : ""}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default TournamentSetup;
